import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Package, Plus, Eye, Trash2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useB2BOrders } from '../../context/B2BOrderContext';
import { bakeryInfo } from '../../data/mock';
import { businessTypes } from '../../data/b2bData';
import { initializeMockData } from '../../utils/mockData';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { orders } = useB2BOrders();
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'customers'
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    fullName: '',
    businessName: '',
    phone: '',
    businessType: '',
    address: '',
    email: ''
  });

  // Get all customers from localStorage
  const customers = JSON.parse(localStorage.getItem('b2b_users') || '[]');

  // Filter orders and customers based on search
  const filteredOrders = orders.filter(order => 
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCustomers = customers.filter(customer =>
    customer.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  const handleAddCustomer = () => {
    if (!newCustomer.fullName || !newCustomer.phone || !newCustomer.businessType) {
      alert('الرجاء إدخال الاسم ورقم الهاتف ونوع النشاط');
      return;
    }

    const customer = {
      id: Date.now().toString(),
      ...newCustomer,
      createdAt: new Date().toISOString()
    };

    const updatedCustomers = [...customers, customer];
    localStorage.setItem('b2b_users', JSON.stringify(updatedCustomers));
    
    setNewCustomer({
      fullName: '',
      businessName: '',
      phone: '',
      businessType: '',
      address: '',
      email: ''
    });
    setShowAddCustomer(false);
    alert('تم إضافة الزبون بنجاح!');
    window.location.reload();
  };

  const handleDeleteCustomer = (customerId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الزبون؟')) {
      const updatedCustomers = customers.filter(c => c.id !== customerId);
      localStorage.setItem('b2b_users', JSON.stringify(updatedCustomers));
      window.location.reload();
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fef3c7', text: '#92400e', label: 'جديد' },
      confirmed: { bg: '#dbeafe', text: '#1e40af', label: 'مؤكد' },
      preparing: { bg: '#fed7aa', text: '#9a3412', label: 'تحت التجهيز' },
      completed: { bg: '#d1fae5', text: '#065f46', label: 'مكتمل' },
      cancelled: { bg: '#fee2e2', text: '#991b1b', label: 'ملغي' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span 
        className="px-3 py-1 rounded-full text-sm font-semibold"
        style={{ background: config.bg, color: config.text }}
      >
        {config.label}
      </span>
    );
  };

  const getBusinessTypeIcon = (type) => {
    const businessType = businessTypes.find(bt => bt.id === type);
    return businessType ? businessType.icon : '🏪';
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)' }}>
      {/* Header */}
      <header className="shadow-lg" style={{ background: 'linear-gradient(90deg, #1a5c1a 0%, #2d7a2d 25%, #3d8b3d 50%, #2d7a2d 75%, #1a5c1a 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={bakeryInfo.logo} 
                alt="Dura Alsham"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-white">لوحة التحكم - Admin</h1>
                <p className="text-sm text-white/80">{bakeryInfo.name}</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-cream-100 text-burgundy-800 hover:bg-cream-200"
            >
              <ArrowLeft className="w-4 h-4 ml-2" />
              الصفحة الرئيسية
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors ${
                activeTab === 'orders'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Package className="w-5 h-5 inline ml-2" />
              الطلبات ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors ${
                activeTab === 'customers'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-5 h-5 inline ml-2" />
              الزبائن ({customers.length})
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-6 flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder={activeTab === 'orders' ? 'البحث عن طلب...' : 'البحث عن زبون...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10 py-3"
            />
          </div>
          {activeTab === 'customers' && (
            <Button
              onClick={() => setShowAddCustomer(true)}
              className="text-white font-semibold"
              style={{ background: '#2d7a2d' }}
            >
              <Plus className="w-5 h-5 ml-2" />
              إضافة زبون
            </Button>
          )}
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">لا توجد طلبات</p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{getBusinessTypeIcon(order.businessType)}</span>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{order.businessName}</h3>
                          <p className="text-sm text-gray-500">{order.customerName}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        📅 {new Date(order.createdAt).toLocaleDateString('ar-EG', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-left">
                      {getStatusBadge(order.status)}
                      <p className="text-2xl font-bold mt-2" style={{ color: '#2d7a2d' }}>
                        €{order.totalAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-2 mb-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span className="text-gray-700">
                          <strong>{item.productName}</strong> × {item.quantity} {item.unit}
                        </span>
                        <span className="font-semibold text-gray-900">€{item.totalPrice.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/b2b/order-confirm/${order.id}`)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 ml-2" />
                      عرض التفاصيل
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-4">
            {filteredCustomers.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">لا يوجد زبائن</p>
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div key={customer.id} className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ background: '#e8f5e9' }}>
                        {getBusinessTypeIcon(customer.businessType)}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{customer.fullName}</h3>
                        <p className="text-gray-600">{customer.businessName}</p>
                        <div className="flex gap-4 mt-2 text-sm text-gray-500">
                          <span>📱 {customer.phone}</span>
                          {customer.email && <span>✉️ {customer.email}</span>}
                        </div>
                        {customer.address && (
                          <p className="text-sm text-gray-500 mt-1">📍 {customer.address}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteCustomer(customer.id)}
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a5c1a' }}>إضافة زبون جديد</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الاسم الكامل *</label>
                <Input
                  value={newCustomer.fullName}
                  onChange={(e) => setNewCustomer({...newCustomer, fullName: e.target.value})}
                  placeholder="مثال: محمد أحمد"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">اسم النشاط / المحل</label>
                <Input
                  value={newCustomer.businessName}
                  onChange={(e) => setNewCustomer({...newCustomer, businessName: e.target.value})}
                  placeholder="مثال: مطعم الشام"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">رقم الهاتف *</label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                  placeholder="+49..."
                  dir="ltr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">نوع النشاط *</label>
                <div className="grid grid-cols-2 gap-2">
                  {businessTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setNewCustomer({...newCustomer, businessType: type.id})}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        newCustomer.businessType === type.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{type.icon}</div>
                      <div className="text-sm font-semibold">{type.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">العنوان</label>
                <Input
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  placeholder="المدينة، الشارع..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">البريد الإلكتروني</label>
                <Input
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                  placeholder="example@email.com"
                  dir="ltr"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleAddCustomer}
                className="flex-1 text-white font-semibold"
                style={{ background: '#2d7a2d' }}
              >
                إضافة
              </Button>
              <Button
                onClick={() => setShowAddCustomer(false)}
                variant="outline"
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
